import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useToast } from '../../contexts/ToastContext';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { displayDateTimeToUTC, utcToDisplayDateAndTime, getTimezoneLabel } from '../../utils/timezoneUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Edit intimation – allowed only when status is PENDING or CLARIFICATION_REQUESTED.
 */
const EditIntimationDialog = ({ open, onClose, intimation, aircraft = [], onSuccess, displayTimezone }) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState('MAINTENANCE');
  const [includedItems, setIncludedItems] = useState('');
  const [excludedItems, setExcludedItems] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledDate, setScheduledDate] = useState(null);
  const [aircraftId, setAircraftId] = useState('');
  const [sourcePlainText, setSourcePlainText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    if (open && intimation) {
      setTitle(intimation.title || '');
      setType(intimation.type || 'MAINTENANCE');
      setIncludedItems(intimation.includedItems || '');
      setExcludedItems(intimation.excludedItems || '');
      setDescription(intimation.description || intimation.intimationDescription || '');
      if (intimation.scheduledDate) {
        const iso = typeof intimation.scheduledDate === 'string' ? intimation.scheduledDate : intimation.scheduledDate?.toISOString?.();
        if (displayTimezone && iso) {
          const { date: d, time: t } = utcToDisplayDateAndTime(iso, displayTimezone);
          setScheduledDate(d && t ? dayjs.tz(`${d}T${t}`, displayTimezone) : (typeof intimation.scheduledDate === 'string' ? new Date(intimation.scheduledDate) : intimation.scheduledDate));
        } else {
          setScheduledDate(typeof intimation.scheduledDate === 'string' ? new Date(intimation.scheduledDate) : intimation.scheduledDate);
        }
      } else {
        setScheduledDate(null);
      }
      setAircraftId((intimation.aircraftId && (intimation.aircraftId._id || intimation.aircraftId)) || '');
      setSourcePlainText(intimation.sourcePlainText || '');
      setError('');
    }
  }, [open, intimation, displayTimezone]);

  const handleSubmit = async () => {
    if (!intimation?._id) return;
    setError('');
    setLoading(true);
    try {
      let scheduledDateISO;
      if (scheduledDate) {
        if (displayTimezone && dayjs.isDayjs(scheduledDate)) {
          scheduledDateISO = displayDateTimeToUTC(scheduledDate.format('YYYY-MM-DD'), scheduledDate.format('HH:mm'), displayTimezone) ?? scheduledDate.toISOString();
        } else {
          scheduledDateISO = scheduledDate.toISOString?.() ?? dayjs(scheduledDate).toISOString();
        }
      } else {
        scheduledDateISO = undefined;
      }
      await axios.put(`/api/intimations/${intimation._id}`, {
        title: title.trim(),
        type,
        includedItems: includedItems.trim(),
        excludedItems: excludedItems.trim() || undefined,
        description: description.trim() || undefined,
        intimationDescription: description.trim() || undefined,
        scheduledDate: scheduledDateISO,
        aircraftId: aircraftId || undefined,
        sourcePlainText: (sourcePlainText || '').trim() || undefined
      });
      showToast('Intimation updated.', 'success');
      onSuccess();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update intimation';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const canEdit = intimation && intimation.status !== 'ACKNOWLEDGED' && intimation.status !== 'COMPLETED' && intimation.status !== 'ARCHIVED';
  if (!intimation) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Intimation</DialogTitle>
      <DialogContent>
        {!canEdit && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This intimation cannot be edited (acknowledged, completed, or archived).
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Aircraft</InputLabel>
              <Select
                value={aircraftId}
                onChange={(e) => setAircraftId(e.target.value)}
                label="Aircraft"
                disabled={!canEdit}
              >
                {aircraft.map((ac) => (
                  <MenuItem key={ac._id} value={ac._id}>
                    {ac.registration} {ac.manufacturer ? `– ${ac.manufacturer}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value)}
                label="Type"
                disabled={!canEdit}
              >
                <MenuItem value="INSPECTION">Inspection</MenuItem>
                <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                <MenuItem value="REPAIR">Defect</MenuItem>
                <MenuItem value="SCHEDULED">Scheduled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title *"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={!canEdit}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Source (plain text)"
              value={sourcePlainText}
              onChange={(e) => setSourcePlainText(e.target.value)}
              disabled={!canEdit}
              helperText="Optional when intimation was created from Upcoming."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Included Items *"
              value={includedItems}
              onChange={(e) => setIncludedItems(e.target.value)}
              disabled={!canEdit}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Excluded Items"
              value={excludedItems}
              onChange={(e) => setExcludedItems(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Additional Description / Remarks"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label={displayTimezone ? `Preferred scheduled date (optional) (${getTimezoneLabel(displayTimezone)})` : 'Preferred scheduled date (optional)'}
              value={scheduledDate ? (dayjs.isDayjs(scheduledDate) ? scheduledDate : dayjs(scheduledDate)) : null}
              onChange={(v) => setScheduledDate(v ? (displayTimezone ? dayjs.tz(v.format('YYYY-MM-DD') + 'T' + v.format('HH:mm'), displayTimezone) : v) : null)}
              slotProps={{ textField: { fullWidth: true } }}
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canEdit || loading || !title.trim() || !includedItems.trim()}
        >
          {loading ? 'Saving...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditIntimationDialog;
